<template>
  <div class="docs">
    <div class="docs-header">
      <h3>
        <span v-if="detail && detail.title">{{ detail.title }}</span>
        <p v-if="detail">
          <i
            >{{ $t('docs_detail_base_createBy') }}: <b>{{ detail.createBy }}</b></i
          >
          <i
            >{{ $t('docs_detail_base_createTime') }}: <b>{{ detail.createTime | formatDate }}</b></i
          >
          <i v-if="detail.updateBy">
            {{ $t('docs_detail_base_updateBy') }}: <b>{{ detail.updateBy }}</b></i
          >
          <i v-if="detail.updateTime">
            {{ $t('docs_detail_base_updateTime') }}: <b>{{ detail.updateTime | formatDate }}</b></i
          >
        </p>
      </h3>
      <DocsOperation
        :record="detail"
        :popover="false"
        :toggle="{
          share: true,
          back: () => {
            this.$router.go(-1);
          },
        }"
      />
    </div>

    <Loading :show="loading" v-if="loading" />
    <div class="docs-detail" v-else>
      <div style="max-width: 70%;margin:0 auto;" v-if="detail && hasReadPermissioin">
        <div id="docDetailContent" class="g-html-content" v-if="detail.content"></div>
        <Empty v-else style="padding-top: 100px;" />
      </div>
      <NoAuth v-else />
    </div>
  </div>
</template>
<script>
import DocsOperation from '@/views/docs/components/Operation';
import * as DocsAPI from '@/api/docs';
import { copyContent } from '@/libs/utils';
import { mapState } from 'vuex';
import { hasReadWritePermission } from '@/libs/permissions';
import docsAuth from '@/views/docs/mixins/docsAuth';
import DOMPurify from 'dompurify';
import { generateSignedUrl } from '@/config/oss';

export default {
  mixins: [docsAuth],
  components: {
    DocsOperation,
  },
  name: 'Docs',
  data() {
    return {
      copyContent,
      id: null,
      type: null,
      detail: null,
      loading: true,
    };
  },
  created() {
    this.id = this.$route.params.id;
    if (!this.id) {
      this.type = this.$route.params.type;
    }
    this.getDetail();
  },
  computed: {
    ...mapState(['userInfo', 'authList']),

    // 是否有数据编辑（包括删除、禁用等）权限
    hasReadPermissioin() {
      return hasReadWritePermission('read', 'wiki', this.detail.readLevel);
    },

    sanitizedHtml() {
      return DOMPurify.sanitize(this.detail.content);
    },
  },
  methods: {
    // 获取文档详情
    getDetail() {
      this.loading = true;
      const params = {};
      if (this.id) {
        params.id = this.id;
      } else if (this.type) {
        params.type = this.type;
      }

      return DocsAPI.getDocumentDetail(params)
        .then(res => {
          if (res.code === 0 && res.data) {
            this.handleDetailContent(res, () => {
              this.loading = false;
              this.$store.commit('setPageLoading', false);
            });
          } else {
            this.detail = null;
            this.loading = false;
            this.$store.commit('setPageLoading', false);
          }
        })
        .catch(() => {
          this.list = null;
          this.loading = false;
          this.$store.commit('setPageLoading', false);
        });
    },

    // 处理详情内容
    handleDetailContent(res, callback) {
      // 对内容进行处理
      const detail = res.data;
      const content = decodeURIComponent((detail.content || '').replace(/\+/g, ' '));

      try {
        (async () => {
          // 获取图片的访问URL
          const parser = new DOMParser();
          const doc = parser.parseFromString(content, 'text/html');

          // 查找所有带有 data-src="image" 的 img 标签
          const images = doc.querySelectorAll('img[data-src="image"]');
          for (const img of images) {
            const url = await generateSignedUrl({ objectKey: img.src });
            img.src = url;
          }

          // 查找所有带有 data-src="video" 的 video 标签
          const videos = doc.querySelectorAll('video[data-src="video"]');
          for (const video of videos) {
            // 处理 video 的 src 属性
            if (video.src) {
              const url = await generateSignedUrl({ objectKey: video.src }); // Resolve src URL
              video.src = url; // Replace with the signed URL
            }

            // 处理 video 内的 source 标签
            const sources = video.querySelectorAll('source');
            console.log('sources', sources);
            for (const source of sources) {
              if (source.src) {
                const url = await generateSignedUrl({ objectKey: source.src }); // Resolve src URL
                source.src = url;
              }
            }
          }

          detail.content = doc.body.innerHTML;
          this.detail = detail;
          this.loading = false;
          this.$nextTick(() => {
            const docDetailContent = document.getElementById('docDetailContent');
            if (docDetailContent) {
              docDetailContent.innerHTML = detail.content;
            }
          });

          callback && callback();
        })();
      } catch (error) {
        console.error('Error while processing document content:', error);
        this.detail = detail;
        callback && callback();
      }
    },
  },
};
</script>
<style lang="less" scoped>
.docs {
  background: @g-layout-background;
  height: 100%;
  .docs-header {
    padding: 12px 24px;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      margin: 0;
      display: flex;
      flex-direction: column;
      span {
        color: #18181a;
        font-size: 24px;
        font-style: normal;
        font-weight: 600;
      }
      p {
        margin: 0;
        padding: 7px 0 0;
        font-size: 12px;
        color: #999;
        i {
          font-style: normal;
          font-weight: 400;
          margin-right: 10px;
          b {
            color: #333;
            font-weight: normal;
          }
        }
      }
    }
  }

  .docs-detail {
    border-radius: 12px;
    background: #fff;
    padding: 24px;
    max-width: 100%;
    overflow: hidden;
    display: grid;
    height: calc(100% - 70px - 24px - 10px);
    overflow-y: auto;
    // 变量定义
    @font-family: 'Arial', sans-serif;
    @font-size: 16px;
    @line-height: 1.5;
    @color: #333;
    @link-color: #007bff;
    @background-color: #f4f4f4;
    @table-border-color: #ddd;
    @max-height: 400px;
  }
}
</style>
